import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Loader2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";

const integrationCards = [
  {
    id: "freshchat",
    title: "Freshchat",
    description: "Omni-channel customer support & live chat",
    icon: <img src="/freshchat.png" alt="Freshchat" className="w-9 h-9 object-contain rounded-md" />,
    route: "/freshchat-config",
    category: "Support",
  },
  {
    id: "freshdesk",
    title: "Freshdesk",
    description: "Customer service software & support ticket system",
    icon: <img src="/freshdesk.png" alt="Freshdesk" className="w-9 h-9 object-contain rounded-md" />,
    route: "/freshdesk-config",
    category: "Helpdesk",
  },
  {
    id: "telegram",
    title: "Telegram",
    description: "High-speed bot integration for instant messaging",
    icon: (
      <svg className="w-9 h-9 text-[#0088cc]" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    ),
    route: "/telegram-config",
    category: "Messaging",
  },
  {
    id: "slack",
    title: "Slack",
    description: "Collaborative AI responses for your workspaces",
    icon: <img src="/slack.svg" alt="Slack" className="w-9 h-9 object-contain rounded-md" />,
    route: "/slack-config",
    category: "Messaging",
  },
  {
    id: "whatsapp",
    title: "WhatsApp",
    description: "Engage users on the world's most popular messaging app",
    icon: (
      <svg className="w-9 h-9 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
    route: "/whatsapp-config",
    category: "Messaging",
  },
  {
    id: "shopify",
    title: "Shopify",
    description: "E-commerce order tracking, lookup & catalog integration",
    icon: <img src="/shopify.png" alt="Shopify" className="w-9 h-9 object-contain rounded-md" />,
    route: "/shopify-config",
    category: "E-commerce",
  },
  {
    id: "email",
    title: "Email",
    description: "Automate inbound email tickets & support replies",
    icon: <img src="/gmail.png" alt="Email" className="w-9 h-9 object-contain rounded-md" />,
    route: "/email-config",
    category: "Support",
  },
  {
    id: "zendesk",
    title: "Zendesk",
    description: "Customer service platform & ticket management",
    icon: <img src="/zendesk.png" alt="Zendesk" className="w-9 h-9 object-contain rounded-md" />,
    route: "/zendesk-config",
    category: "Helpdesk",
  },
];

const Integrations = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statuses, setStatuses] = useState({
    freshchat: false,
    freshdesk: false,
    telegram: false,
    slack: false,
    whatsapp: false,
    email: false,
    zendesk: false,
    shopify: false,
  });
  const [configs, setConfigs] = useState({});
  const [togglingId, setTogglingId] = useState(null);

  const checkStatuses = useCallback(async () => {
    const agentId = activeAiAgentId || localStorage.getItem("activeAiAgentId");
    const options = { aiAgentId: agentId };

    const checks = [
      {
        key: "freshchat",
        req: ApiClient.request("/freshchat/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.freshchat_url || res?.config?.api_token || res?.config?.app_id);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "freshdesk",
        req: ApiClient.request("/freshdesk/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.freshdesk_domain || res?.config?.api_key);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "telegram",
        req: ApiClient.request("/telegram/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.bot_token);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "slack",
        req: ApiClient.request("/slack/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.bot_token || res?.config?.signing_secret);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "whatsapp",
        req: ApiClient.request("/whatsapp/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.access_token || res?.config?.phone_number_id);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "email",
        req: ApiClient.request("/email/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.google_connected || res?.config?.smtp_host || res?.config?.imap_host);
          return {
            active: Boolean(res?.success && (res?.config?.is_active || res?.config?.google_connected)),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "zendesk",
        req: ApiClient.request("/zendesk/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.subdomain || res?.config?.api_token);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "shopify",
        req: ApiClient.request("/shopify/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.shop_url || res?.config?.access_token);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
    ];

    const results = await Promise.allSettled(
      checks.map(async (c) => {
        try {
          const data = await c.req;
          return { key: c.key, ...data };
        } catch {
          return { key: c.key, active: false, configured: false, config: {} };
        }
      })
    );

    const updatedStatuses = {};
    const updatedConfigs = {};

    results.forEach((r) => {
      if (r.status === "fulfilled" && r.value) {
        updatedStatuses[r.value.key] = Boolean(r.value.active);
        updatedConfigs[r.value.key] = {
          configured: Boolean(r.value.configured),
          data: r.value.config,
        };
      }
    });

    setStatuses((prev) => ({ ...prev, ...updatedStatuses }));
    setConfigs(updatedConfigs);
  }, [activeAiAgentId]);

  useEffect(() => {
    checkStatuses();
  }, [checkStatuses]);

  const handleToggle = async (card, e) => {
    e.stopPropagation();
    const isCurrentActive = Boolean(statuses[card.id]);
    const nextState = !isCurrentActive;
    const cardConfig = configs[card.id];

    if (nextState && (!cardConfig || !cardConfig.configured)) {
      showErrorToast(`Please configure ${card.title} credentials before activating.`);
      navigate(card.route);
      return;
    }

    setTogglingId(card.id);
    const agentId = activeAiAgentId || localStorage.getItem("activeAiAgentId");

    try {
      let endpoint = `/${card.id}/config`;
      let bodyData = { is_active: nextState };

      // Ensure required preserved fields where backend validation requires them
      if (card.id === "freshdesk" && cardConfig?.data) {
        bodyData = {
          ...cardConfig.data,
          is_active: nextState,
        };
      }

      const res = await ApiClient.request(endpoint, {
        method: "PUT",
        body: JSON.stringify(bodyData),
        aiAgentId: agentId,
      });

      if (res?.success || res?.status === "success" || res?.is_active !== undefined) {
        setStatuses((prev) => ({ ...prev, [card.id]: nextState }));
        showSuccessToast(`${card.title} integration ${nextState ? "activated" : "deactivated"}`);
      } else {
        showErrorToast(res?.message || `Failed to update ${card.title} status`);
      }
    } catch (err) {
      showErrorToast(err?.message || `Failed to update ${card.title}`);
    } finally {
      setTogglingId(null);
    }
  };

  const filteredCards = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return integrationCards;
    return integrationCards.filter(
      (card) =>
        card.title.toLowerCase().includes(query) ||
        card.description.toLowerCase().includes(query) ||
        (card.category && card.category.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header - Styled like EasyVoice */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Integrations</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">Connect your helpdesk, CRM, or messaging channels.</p>
          </div>
          <div className="text-[11px] sm:text-xs text-zinc-400 font-medium self-start sm:self-auto">
            {integrationCards.length} integrations available
          </div>
        </div>

        {/* Search Bar */}
        <div className="pt-4 sm:pt-5 pb-3">
          <div className="relative w-full sm:max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
              <Search size={14} strokeWidth={2.5} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search integrations..."
              className="w-full text-xs border border-zinc-200 rounded-lg pl-9 pr-3 py-2.5 focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-800 font-medium placeholder-zinc-400 shadow-sm"
            />
          </div>
        </div>

        {/* Cards Grid */}
        <div className="py-2 sm:py-4">
          {filteredCards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
              <h3 className="text-sm font-semibold text-zinc-900">No integrations found</h3>
              <p className="text-xs text-zinc-500 mt-1">
                No matching integration for "{searchQuery}". Clear your search query to see all options.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-5">
              {filteredCards.map((card) => {
                const isConnected = Boolean(statuses[card.id]);
                const isConfigured = Boolean(configs[card.id]?.configured);
                const isToggling = togglingId === card.id;

                return (
                  <div
                    key={card.id}
                    onClick={() => navigate(card.route)}
                    className="group bg-white border border-zinc-200 hover:border-zinc-350 rounded-xl p-4 sm:p-5 transition-all duration-200 hover:shadow-sm flex flex-col justify-between cursor-pointer active:scale-[0.99]"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center shrink-0 transition-transform group-hover:scale-105 duration-200">
                          {card.icon}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-zinc-500 bg-zinc-100/80 px-2 py-0.5 rounded-md">
                            {card.category}
                          </span>
                          {/* Toggle Switch */}
                          <button
                            type="button"
                            onClick={(e) => handleToggle(card, e)}
                            disabled={isToggling}
                            title={
                              !isConfigured
                                ? "Configure credentials to activate"
                                : isConnected
                                ? "Deactivate integration"
                                : "Activate integration"
                            }
                            className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer focus:outline-none ${
                              isConnected ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-300"
                            } ${isToggling ? "opacity-60 cursor-wait" : ""}`}
                          >
                            {isToggling ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Loader2 size={10} className="animate-spin text-zinc-400" />
                              </div>
                            ) : (
                              <div
                                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-xs ${
                                  isConnected ? "left-[18px]" : "left-0.5"
                                }`}
                              />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 sm:mt-3.5">
                        <h3 className="text-sm font-bold text-zinc-900 group-hover:text-black transition-colors">
                          {card.title}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1 sm:mt-1.5 leading-relaxed line-clamp-2">
                          {card.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-zinc-100 mt-4 sm:mt-5 pt-3 sm:pt-3.5">
                      {isConnected ? (
                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Active
                        </span>
                      ) : isConfigured ? (
                        <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-400"></span>
                          Inactive
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-300"></span>
                          Ready to connect
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(card.route);
                        }}
                        className="px-3 py-1.5 bg-zinc-50 hover:bg-zinc-900 text-zinc-700 hover:text-white rounded-lg text-xs font-semibold transition-all duration-150 flex items-center gap-1.5 group-hover:bg-zinc-900 group-hover:text-white"
                      >
                        <span>Configure</span>
                        <ArrowRight size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Integrations;
